import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner"; // assuming you're using sonner with shadcn

export interface UseCopyOptions {
  /**
   * Duration in milliseconds for how long the copied state should remain true
   * @default 2000
   */
  copiedTimeout?: number;
  /**
   * Success message to show in toast
   * @default "Copied to clipboard!"
   */
  successMessage?: string;
  /**
   * Error message to show in toast
   * @default "Failed to copy to clipboard"
   */
  errorMessage?: string;
}

/**
 * The return type for useCopy hook.
 */
export interface UseCopyResult {
  /**
   * Whether the last copy action remains true until timeout.
   */
  readonly copied: boolean;
  /**
   * Function to copy text to clipboard.
   * @param text - text to copy
   * @returns Promise that resolves when copy completes
   */
  readonly copy: (text: string) => Promise<void>;
}

/**
 * A hook that provides clipboard copy functionality with toast notifications
 * and a temporary copied state indicator
 *
 * @param options - configuration options for the hook
 * @returns {UseCopyResult} copied state and copy function
 */
export function useCopy(options: UseCopyOptions = {}): UseCopyResult {
  const {
    copiedTimeout = 2000,
    successMessage = "Copied to clipboard!",
    errorMessage = "Failed to copy to clipboard",
  } = options;

  const [copied, setCopied] = useState(false);

  // Ref to track timeout for resetting copied state
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear timeout on unmount to avoid setting state on unmounted component
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const copy = useCallback(
    async (text: string) => {
      // Guard for unsupported Clipboard API
      if (!navigator.clipboard?.writeText) {
        const msg = "Clipboard API is not supported";
        toast.error(msg);
        return;
      }
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success(successMessage);

        // Clear any existing timeout to avoid multiple timers
        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current);
        }
        // Reset copied state after timeout
        timeoutRef.current = setTimeout(() => {
          setCopied(false);
        }, copiedTimeout);
      } catch (error: unknown) {
        if (error instanceof Error) {
        } else {
        }
        toast.error(errorMessage);
        setCopied(false);
      }
    },
    [copiedTimeout, successMessage, errorMessage],
  );

  return {
    copied,
    copy,
  };
}
